/**
 * InspectionCard   – Card wrapper with colored top border
 * InspectionCardHeader – Icon + title + branch/contract metadata row
 * InspectionCardFooter – Sticky cancel / save footer
 *
 * All three are driven by an InspectionColorSet so every page has
 * a single source of truth for its accent color.
 */

import { ArrowLeft } from 'lucide-react'
import type * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Card, CardFooter, CardHeader } from '@/components/ui/card'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { cn } from '@/lib/utils'
import { useAuthStore, useProfileStore } from '@/store'
import type { OrgCR } from '@/types/organization'
import type { InspectionColorSet } from '../../constants/inspection-colors'

/* ── InspectionCard ──────────────────────────────────────────────── */

interface InspectionCardProps {
	colors: InspectionColorSet
	children: React.ReactNode
}

export const InspectionCard = ({ colors, children }: InspectionCardProps) => (
	<Card
		className={cn('rounded-none w-full border-t-4 gap-0 py-0', colors.border)}
	>
		{children}
	</Card>
)

/* ── InspectionCardHeader ────────────────────────────────────────── */

interface InspectionCardHeaderProps {
	/** Lucide (or any) icon node — sized/colored inside the component */
	icon: React.ReactNode
	title: string
	colors: InspectionColorSet

	// ── Inspection mode: branch / contract metadata row ──
	branch?: string //TODO check possibility to delete in a future refactor
	branchId?: string
	branchList?: Array<{ id: string; name: string; crs?: OrgCR[] }>
	onBranchChange?: (id: string) => void
	crList?: OrgCR[]
	crId?: string
	onCrChange?: (id: string) => void

	// ── Admin / detail mode: back button + subtitle + right-side action slot ──
	/** Renders an ArrowLeft back button before the icon when provided */
	onBack?: () => void
	/** Secondary line rendered below the title */
	subtitle?: string
	/** Right-side slot — e.g. a "+ New" button */
	actions?: React.ReactNode
}

export const InspectionCardHeader = ({
	icon,
	title,
	colors,
	branch,
	branchId,
	branchList,
	onBranchChange,
	crList,
	crId,
	onCrChange,
	onBack,
	subtitle,
	actions,
}: InspectionCardHeaderProps) => {
	const { t } = useTranslation()
	const userRole = useAuthStore((s) => s.user?.role)
	const setProfileBranch = useProfileStore((s) => s.setDefaultBranch)
	const setProfileContract = useProfileStore((s) => s.setDefaultContract)
	const setProfileCustomer = useProfileStore((s) => s.setDefaultCustomer)
	const profileCustomerId = useProfileStore((s) => s.defaultCustomerId)
	const canChangeContract =
		userRole === 'ADMIN' ||
		userRole === 'MANAGER' ||
		userRole === 'SSMA_MANAGER' ||
		userRole === 'TECHNICIAN' ||
		userRole === 'LEADER'

	const canChangeBranch =
		userRole === 'ADMIN' ||
		userRole === 'MANAGER' ||
		userRole === 'SSMA_MANAGER'

	const hasBranchRow = branch !== undefined

	const handleBranchSelectChange = (newBranchId: string) => {
		if (!branchList) {
			onBranchChange?.(newBranchId)
			return
		}

		const selected = branchList.find((item) => item.id === newBranchId)
		setProfileBranch(newBranchId, selected?.name ?? '')

		const firstCr = selected?.crs?.[0]
		setProfileContract(firstCr?.id ?? '', firstCr?.name ?? '')

		// set default customer based on first CR if available
		const firstCustomer = firstCr?.customers?.[0]
		setProfileCustomer(firstCustomer?.id ?? '', firstCustomer?.name ?? '')

		onBranchChange?.(newBranchId)
	}

	const selectedCrCustomers =
		crList?.find((c) => c.id === crId)?.customers ?? []

	const handleCrSelectChange = (newCrId: string) => {
		const selectedCr = crList?.find((item) => item.id === newCrId)
		setProfileContract(newCrId, selectedCr?.name ?? '')

		// set default customer based on selected CR
		const firstCustomer = selectedCr?.customers?.[0]
		setProfileCustomer(firstCustomer?.id ?? '', firstCustomer?.name ?? '')
		onCrChange?.(newCrId)
	}

	const handleCustomerSelectChange = (newCustomerId: string) => {
		const selectedCr = crList?.find((item) => item.id === crId)
		const selectedCustomer = selectedCr?.customers?.find(
			(c) => c.id === newCustomerId,
		)
		setProfileCustomer(newCustomerId, selectedCustomer?.name ?? '')
	}

	return (
		<CardHeader
			className={cn(
				'bg-linear-to-b to-transparent px-4 pt-5 sm:px-6',
				hasBranchRow ? 'pb-6' : 'pb-4',
				colors.headerBg,
			)}
		>
			<div className="flex flex-wrap items-center justify-between gap-3 sm:gap-4">
				{/* Left: [back button] + icon + title [+ subtitle] */}
				<div className="flex items-center gap-3">
					{onBack && (
						<Button
							type="button"
							variant="ghost"
							size="icon"
							onClick={onBack}
							className="shrink-0 text-kaefer-dark-grey"
						>
							<ArrowLeft className="size-5" />
						</Button>
					)}
					<div
						className={cn(
							'flex size-10 shrink-0 items-center justify-center rounded-lg shadow-sm',
							colors.iconBg,
						)}
					>
						<span className={colors.iconText}>{icon}</span>
					</div>
					<div className={cn(hasBranchRow && 'mb-4 sm:mb-0')}>
						<h1 className="text-lg font-bold text-kaefer-dark-grey sm:text-xl">
							{title}
						</h1>
						{subtitle && (
							<p className="text-xs text-kaefer-medium-grey">{subtitle}</p>
						)}
					</div>
				</div>

				{/* Right: branch+contract grid (inspection) OR actions slot (admin) */}
				{hasBranchRow ? (
					<div className="flex items-center w-full gap-3 sm:w-full justify-between">
						<div
							className={cn(
								'rounded-md px-3 py-1 grid gap-4 text-sm flex-1',
								selectedCrCustomers.length > 0 ? 'grid-cols-3' : 'grid-cols-2',
							)}
						>
							<div className="flex flex-col items-start">
								<span className="font-semibold text-muted-foreground">
									{t('inspections.common.branch')}
								</span>
								{branchList ? (
									<NativeSelect
										value={branchId ?? ''}
										onChange={(e) => handleBranchSelectChange(e.target.value)}
										disabled={
											!canChangeBranch || !branchList || branchList.length === 0
										}
										className="h-7 w-full max-w-full border-none bg-transparent px-1 py-0 pr-9 text-sm shadow-none text-left truncate focus:ring-0"
									>
										{branchList.length === 0 ? (
											<NativeSelectOption value="" disabled>
												{t('inspections.common.noBranches')}
											</NativeSelectOption>
										) : (
											branchList.map((item) => (
												<NativeSelectOption key={item.id} value={item.id}>
													{item.name}
												</NativeSelectOption>
											))
										)}
									</NativeSelect>
								) : (
									<span className="font-normal">{branch}</span>
								)}
							</div>
							<div className="flex flex-col items-start flex-1">
								<span className="font-semibold text-muted-foreground">
									{t('inspections.common.contract')}
								</span>
								<NativeSelect
									value={crId ?? ''}
									onChange={(e) => handleCrSelectChange(e.target.value)}
									disabled={
										!canChangeContract || !crList || crList.length === 0
									}
									className="h-7 w-full max-w-full border-none bg-transparent px-1 py-0 pr-9 text-sm shadow-none text-left truncate focus:ring-0"
								>
									{!crList || crList.length === 0 ? (
										<NativeSelectOption value="" disabled>
											{t('inspections.common.noContracts')}
										</NativeSelectOption>
									) : (
										crList.map((item) => (
											<NativeSelectOption key={item.id} value={item.id}>
												{item.name}
											</NativeSelectOption>
										))
									)}
								</NativeSelect>
							</div>

							{/* Customer select: rendered when selected CR has customers */}
							{selectedCrCustomers.length > 0 && (
								<div className="flex flex-col items-start flex-1">
									<span className="font-semibold text-muted-foreground">
										{t('inspections.common.customer')}
									</span>
									<NativeSelect
										value={profileCustomerId ?? ''}
										onChange={(e) => handleCustomerSelectChange(e.target.value)}
										className="h-7 w-full max-w-full border-none bg-transparent px-1 py-0 pr-9 text-sm shadow-none text-left truncate focus:ring-0"
									>
										{selectedCrCustomers.length === 0 ? (
											<NativeSelectOption value="" disabled>
												{t('inspections.common.noCustomers')}
											</NativeSelectOption>
										) : (
											selectedCrCustomers.map((cust) => (
												<NativeSelectOption key={cust.id} value={cust.id}>
													{cust.name}
												</NativeSelectOption>
											))
										)}
									</NativeSelect>
								</div>
							)}
						</div>
					</div>
				) : actions ? (
					actions
				) : null}
			</div>
		</CardHeader>
	)
}

/* ── InspectionCardFooter ────────────────────────────────────────── */

interface InspectionCardFooterProps {
	colors: InspectionColorSet
	isSubmitting: boolean
	onCancel: () => void
	cancelLabel: string
	saveLabel: string
	/** Shown instead of saveLabel while isSubmitting is true */
	savingLabel?: string
	wasExpired?: boolean
}

export const InspectionCardFooter = ({
	colors,
	isSubmitting,
	onCancel,
	cancelLabel,
	saveLabel,
	savingLabel,
	wasExpired = false,
}: InspectionCardFooterProps) => (
	<CardFooter
		className={cn(
			'sticky bottom-0 sm:static sm:flex sm:justify-end bg-white border-t gap-3 py-3 z-10',
			colors.footerBorder,
		)}
	>
		<Button
			type="button"
			variant="cancel"
			onClick={onCancel}
			disabled={isSubmitting}
			className="flex-1 sm:hidden sm:flex-none"
		>
			{cancelLabel}
		</Button>
		<Button
			type="submit"
			disabled={isSubmitting || wasExpired}
			className={cn(
				'flex-1 sm:flex-none sm:min-w-40 font-semibold',
				colors.saveBtn,
			)}
		>
			{isSubmitting && savingLabel ? savingLabel : saveLabel}
		</Button>
	</CardFooter>
)
